import { api, apiClient } from '@/lib/axios'
import type {
  BrandIdentity,
  ImagePlaygroundRequest,
  ImagePlaygroundResponse,
  ReferenceImage,
  UpsertBrandIdentityDto,
} from '../types/brand-identity.types'

export async function getBrandIdentity(): Promise<BrandIdentity | null> {
  const response = await api.get<BrandIdentity | null>('/api/brand-identity')
  if (!response.success) throw new Error(response.error.message)
  return response.data
}

export async function upsertBrandIdentity(data: UpsertBrandIdentityDto): Promise<BrandIdentity> {
  const response = await api.put<BrandIdentity>('/api/brand-identity', data)
  if (!response.success) throw new Error(response.error.message)
  return response.data
}

export async function uploadBrandLogo(file: File): Promise<BrandIdentity> {
  const formData = new FormData()
  formData.append('logo', file)

  const response = await apiClient.put('/api/brand-identity/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to upload logo')
  }

  return response.data.data
}

export async function generatePlaygroundImage(
  data: ImagePlaygroundRequest,
): Promise<ImagePlaygroundResponse> {
  const formData = new FormData()
  formData.append('title', data.title)
  if (data.imageStyle) formData.append('imageStyle', data.imageStyle)
  if (data.userPrompt) formData.append('userPrompt', data.userPrompt)
  if (data.referenceImages) {
    data.referenceImages.forEach((file) => formData.append('referenceImages', file))
  }
  if (data.referenceImageIds?.length) {
    formData.append('referenceImageIds', JSON.stringify(data.referenceImageIds))
  }

  const response = await apiClient.post('/api/brand-identity/image-playground', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to generate playground image')
  }

  return response.data.data
}

export async function getReferenceImages(): Promise<ReferenceImage[]> {
  const response = await api.get<ReferenceImage[]>('/api/brand-identity/reference-images')
  if (!response.success) throw new Error(response.error.message)
  return response.data
}

export async function uploadReferenceImage(file: File): Promise<ReferenceImage> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await apiClient.post('/api/brand-identity/reference-images', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to upload reference image')
  }

  return response.data.data
}

export async function deleteReferenceImage(id: string): Promise<void> {
  const response = await api.delete(`/api/brand-identity/reference-images/${id}`)
  if (!response.success) throw new Error(response.error.message)
}
